import { useState } from 'react'
import { Brand, Icon } from '../components/brand'
import { ApiError } from '../lib/api'
import { useSession } from '../lib/session'
import { Button, Field, Input } from '../components/ui'

/**
 * Two accounts, printed on the page.
 *
 * This is a demonstration deployment holding generated data, and hiding
 * throwaway credentials from a reviewer helps nobody. Printing them also makes
 * the point the roles exist for: the same screens, and one of these two cannot
 * change what anybody earns.
 */
const DEMO_ACCOUNTS = [
  {
    email: 'hr.admin@acme.example',
    password: 'demo-admin-2025',
    role: 'HR administrator',
    can: 'Record raises, hire, import a pay round',
  },
  {
    email: 'hr.viewer@acme.example',
    password: 'demo-viewer-2025',
    role: 'Compensation analyst',
    can: 'See every figure, change nothing',
  },
] as const

export function SignIn() {
  const { signIn } = useSession()
  // Typed, because `as const` on the accounts above otherwise narrows these to
  // the one literal they were initialised with.
  const [email, setEmail] = useState<string>(DEMO_ACCOUNTS[0].email)
  const [password, setPassword] = useState<string>(DEMO_ACCOUNTS[0].password)
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(undefined)

    try {
      await signIn(email, password)
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not reach the server. Try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sign-in">
      <aside className="sign-in-story">
        <Brand />
        <h1>
          Every person.
          <br />
          Every pay decision.
        </h1>
        <p>
          A clear view of compensation across your organisation, today and at any point in its
          history.
        </p>
        <div className="sign-in-art" aria-hidden="true">
          <header>
            <strong>Compensation, in perspective</strong>
            <span>Illustration</span>
          </header>
          {[
            ['Level 3', '55%'],
            ['Level 4', '75%'],
            ['Level 5', '95%'],
          ].map(([label, width]) => (
            <div className="illustrated-range" key={label}>
              <span>{label}</span>
              <div>
                <i style={{ width }} />
              </div>
            </div>
          ))}
          <footer>
            <span>Published salary ranges</span>
            <span>● Employee salary</span>
          </footer>
        </div>
        <div className="sign-in-story-note">
          10,000 people. Nine countries. One clear perspective.
        </div>
      </aside>
      <main className="sign-in-main">
        <div className="sign-in-form">
          <Brand />
          <h2>Welcome back.</h2>
          <p className="sign-in-subtitle">Sign in to your compensation workspace.</p>
          <form onSubmit={(event) => void submit(event)} className="space-y-5" noValidate>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                autoComplete="username"
                required
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'sign-in-error' : undefined}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <div className="relative">
              <Field label="Password">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  autoComplete="current-password"
                  required
                  className="pr-12"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'sign-in-error' : undefined}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
              <button
                type="button"
                className="absolute bottom-0 right-0 flex size-12 items-center justify-center rounded-lg text-ink-faint hover:text-accent focus-visible:outline-2 focus-visible:outline-accent"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
              >
                <Icon name="eye" />
              </button>
            </div>
            {error ? (
              <p
                id="sign-in-error"
                role="alert"
                className="rounded-lg border border-low/20 bg-low-soft px-4 py-3 text-xs text-low"
              >
                {error}
              </p>
            ) : null}
            <Button type="submit" variant="primary" disabled={busy} className="sign-in-submit">
              {busy ? 'Signing in…' : 'Sign in'}
              <Icon name="arrow" />
            </Button>
          </form>
          <div className="demo-accounts">
            <p>Explore with a demo account</p>
            <div className="demo-account-options">
              {DEMO_ACCOUNTS.map((account) => (
                <button
                  key={account.email}
                  type="button"
                  className="demo-account focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  aria-pressed={email === account.email}
                  disabled={busy}
                  onClick={() => {
                    setEmail(account.email)
                    setPassword(account.password)
                    setError(undefined)
                  }}
                >
                  <strong>{account.role}</strong>
                  <small>{account.can}</small>
                </button>
              ))}
            </div>
          </div>
          <p className="sign-in-footnote">
            Demonstration workspace. All employee data is generated.
            <br />
            Choose an account above to fill in its sign-in details.
          </p>
        </div>
      </main>
    </div>
  )
}
